import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api, INVENTORY_STATUSES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Plus, Box, MapPin, ArrowLeft, Trash2, QrCode, Pencil } from "lucide-react";
import AttachToProject from "../components/machines/AttachToProject";
import QrPanel from "../components/QrPanel";
import VisibilityPicker from "../components/VisibilityPicker";
import StatusBadge from "../components/StatusBadge";
import NextActionsPanel from "../components/NextActionsPanel";

const STATUS_KEY = { ledig: "Ledig", ute: "Ute", trasig: "Trasig" };
const EMPTY_INV_FORM = { namn: "", typ: "", plats: "", status: "ledig" };

export function InventoryList() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ status: "" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_INV_FORM);
  const [searchParams, setSearchParams] = useSearchParams();
  const [justSaved, setJustSaved] = useState(false);
  const [savedId, setSavedId] = useState(null);

  const load = async () => {
    const params = filter.status ? { status: filter.status } : {};
    const { data } = await api.get("/inventories", { params });
    setItems(data || []);
  };
  useEffect(() => { if (user) load(); }, [user, filter.status]);

  // Pre-load edit form via deep-link ?edit=<inv_id>
  useEffect(() => {
    const editParam = searchParams.get("edit");
    if (!editParam || !user) return;
    api.get(`/inventories/${editParam}`)
      .then((r) => startEdit(r.data))
      .catch(() => toast.error(t("dash.failed")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;

  const startEdit = (i) => {
    setEditingId(i.id);
    setForm({ namn: i.namn || "", typ: i.typ || "", plats: i.plats || "", status: i.status || "ledig" });
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_INV_FORM);
    if (searchParams.get("edit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      let newId = null;
      if (editingId) {
        await api.patch(`/inventories/${editingId}`, form);
        toast.success(t("inv.updated"));
        newId = editingId;
      } else {
        const { data } = await api.post("/inventories", form);
        toast.success(t("inv.created"));
        newId = data?.id || null;
      }
      cancelForm();
      await load();
      if (newId) {
        setSavedId(newId);
        setJustSaved(true);
      }
    } catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  const canCreate = user.role === "seller" || user.role === "admin";

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-tech">{t("inv.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("inv.title")}</h1>
          </div>
          {canCreate && (
            <button onClick={() => { if (showForm) { cancelForm(); } else { setShowForm(true); } }} className="btn-primary" data-testid="inv-new-btn"><Plus size={14} /> {t("inv.new")}</button>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          <select className="input-tech max-w-[200px]" value={filter.status} onChange={(e) => setFilter({ status: e.target.value })} data-testid="inv-filter-status">
            <option value="">{t("inv.status")}: —</option>
            {INVENTORY_STATUSES.map((s) => <option key={s} value={s}>{t(`inv.status${STATUS_KEY[s]}`)}</option>)}
          </select>
        </div>

        {showForm && (
          <form onSubmit={create} className="mt-6 border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="inv-form">
            <div className="md:col-span-3 label-tech">{editingId ? t("inv.editTitle") : t("inv.newTitle")}</div>
            <div><div className="label-tech mb-2">{t("inv.namn")}</div><input className="input-tech" value={form.namn} onChange={(e) => setForm({ ...form, namn: e.target.value })} required data-testid="inv-namn-input" /></div>
            <div><div className="label-tech mb-2">{t("inv.typ")}</div><input className="input-tech" value={form.typ} onChange={(e) => setForm({ ...form, typ: e.target.value })} data-testid="inv-typ-input" /></div>
            <div><div className="label-tech mb-2">{t("inv.plats")}</div><input className="input-tech" value={form.plats} onChange={(e) => setForm({ ...form, plats: e.target.value })} data-testid="inv-plats-input" /></div>
            {editingId && (
              <div><div className="label-tech mb-2">{t("inv.status")}</div>
                <select className="input-tech" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="inv-status-select">
                  {INVENTORY_STATUSES.map((s) => <option key={s} value={s}>{t(`inv.status${STATUS_KEY[s]}`)}</option>)}
                </select>
              </div>
            )}
            <div className="md:col-span-3 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={cancelForm}>{t("admin.orgs.cancel")}</button>
              <button type="submit" className="btn-primary" data-testid="inv-submit-btn">{t("admin.orgs.save")}</button>
            </div>
          </form>
        )}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          {items.length === 0 ? (
            <div className="bg-[var(--surface)] p-10 label-tech md:col-span-2 lg:col-span-3">{t("inv.empty")}</div>
          ) : items.map((i) => (
            <Link key={i.id} to={`/inventory/${i.id}`} className="bg-[var(--surface)] p-5 hover:bg-[var(--surface-alt)] transition-colors block" data-testid={`inv-card-${i.id}`}>
              <div className="flex items-center gap-2 label-tech"><Box size={12} /> {i.typ || "—"}</div>
              <div className="font-display text-lg mt-2">{i.namn}</div>
              <div className="mt-2 flex items-center gap-3 text-sm text-[var(--ink-700)]">
                <span className="inline-flex items-center gap-1"><MapPin size={12} /> {i.plats || "—"}</span>
                <StatusBadge status={i.status} />
              </div>
            </Link>
          ))}
        </div>

        {justSaved && savedId && (
          <div className="mt-8">
            <NextActionsPanel
              resourceType="inventory"
              resourceId={savedId}
              onShare={() => {
                setJustSaved(false);
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="visibility-picker"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 50);
              }}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}

export function InventoryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [showQr, setShowQr] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/inventories/${id}`);
      setItem(data);
    } catch { toast.error(t("detail.notFound")); navigate("/inventory"); }
  }, [id, navigate, t]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;
  if (!item) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;

  const isOwner = item.owner_id === user.user_id || user.role === "admin";

  const updateStatus = async (newStatus) => {
    try {
      await api.patch(`/inventories/${item.id}`, {
        namn: item.namn, typ: item.typ, plats: item.plats, status: newStatus, project_id: item.project_id,
      });
      toast.success(t("inv.updated"));
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  const del = async () => {
    if (!confirm(t("dash.confirmDelete"))) return;
    try { await api.delete(`/inventories/${item.id}`); toast.success(t("inv.deleted")); navigate("/inventory"); }
    catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  return (
    <Layout>
      <div className="max-w-[1100px] mx-auto px-6 pt-8 pb-16">
        <Link to="/inventory" className="label-tech hover:text-[var(--ink)] inline-flex items-center gap-1" data-testid="inv-back-link">
          <ArrowLeft size={12} /> {t("nav.backToList")}
        </Link>

        <div className="mt-4 grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          <div className="col-span-12 lg:col-span-7 bg-[var(--bone)] p-6">
            <div className="label-tech inline-flex items-center gap-2"><Box size={12} /> {item.typ || "—"}</div>
            <h1 className="font-display text-3xl tracking-tight mt-2">{item.namn}</h1>
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-[var(--ink-700)]">
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {item.plats || "—"}</span>
              <StatusBadge status={item.status} size="lg" />
              {item.organization_id && item.organization_id !== user.organization_id && (
                <span className="tag bg-[var(--brick)] text-[var(--bone)]" data-testid="external-resource-tag">{t("vis.externalTag")}</span>
              )}
              {item.visibility === "shared" && (
                <span className="tag bg-[var(--moss)] text-[var(--bone)]" data-testid="shared-tag">{t("vis.sharedTag")}</span>
              )}
            </div>

            {isOwner && (
              <div className="mt-6 flex flex-wrap gap-2">
                {INVENTORY_STATUSES.map((s) => (
                  <button key={s} onClick={() => updateStatus(s)} className={`btn-ghost ${item.status === s ? "bg-[var(--ink)] text-[var(--bone)]" : ""}`} data-testid={`inv-status-${s}-btn`}>
                    {t(`inv.status${STATUS_KEY[s]}`)}
                  </button>
                ))}
                <button className="btn-ghost text-[var(--brick)]" onClick={del} data-testid="inv-delete-btn"><Trash2 size={12} /> {t("inv.deleted")}</button>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button className="btn-ghost" onClick={() => setShowQr(!showQr)} data-testid="inv-qr-btn">
                <QrCode size={14} /> {t("qr.title")}
              </button>
              {isOwner && (
                <button className="btn-ghost" onClick={() => navigate(`/inventory?edit=${item.id}`)} data-testid="inv-edit-btn">
                  <Pencil size={14} /> {t("detail.edit")}
                </button>
              )}
            </div>

            {showQr && (
              <div className="mt-4">
                <QrPanel
                  defaultMode="detail"
                  resourceName={item.namn}
                  urls={{
                    detail: { url: `${window.location.origin}/inventory/${item.id}`, label: t("qr.modeDetail"), hint: t("qr.scanHint") },
                  }}
                />
              </div>
            )}

            <div className="mt-6">
              <AttachToProject objectType="inventory" objectId={item.id} currentProjectId={item.project_id} onChange={load} />
            </div>

            {isOwner && (
              <div className="mt-4">
                <VisibilityPicker
                  resourceType="inventory"
                  resourceId={item.id}
                  currentVisibility={item.visibility}
                  currentSharedWith={item.shared_with}
                  onChange={load}
                />
              </div>
            )}
          </div>

          <div className="col-span-12 lg:col-span-5 bg-[var(--surface)] p-6">
            <div className="label-tech">{t("inv.history")}</div>
            <div className="mt-3 divide-y divide-[var(--ink-200)]">
              {(!item.historik || item.historik.length === 0) && <div className="label-tech py-3">—</div>}
              {(item.historik || []).map((h) => (
                <div key={`${h.at}-${h.by}`} className="py-2 text-sm">
                  <div>{(STATUS_KEY[h.from] || h.from) + " → " + (STATUS_KEY[h.to] || h.to)}</div>
                  <div className="label-tech">{h.by_name} · {new Date(h.at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
